import {
  Avatar,
  Box,
  Flex,
  FlexProps,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  VStack,
  useColorModeValue,
  Badge,
} from '@chakra-ui/react';
import { useUserOperations } from '@grcs/hook/useDatabase';
import { useLocalStorage } from '@grcs/hook/useLocalStorage';
import { UserRole } from '@grcs/types/UserRole';
import {
  FiBell,
  FiChevronDown,
  FiLogOut,
  FiMenu,
  FiSettings,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { TABLE_NAMES } from '@grcs/utils/database/types';

interface IMobileProps extends FlexProps {
  onOpen: () => void;
}

export const MobileNav: React.FC<IMobileProps> = ({ onOpen, ...rest }) => {
  const navigate = useNavigate();
  const userOps = useUserOperations();
  const [{ first_name, full_name, role, user_token }, setUser] =
    useLocalStorage('user', {});
  const [unreadCount, setUnreadCount] = useState(0);

  const handleSignOut = async () => {
    setUser({});
    await userOps.signOut();
    navigate('/');
  };

  const handleUpdatePassword = () => {
    const base =
      role === UserRole.ADMIN
        ? 'admin'
        : role === UserRole.TEACHER
        ? 'teacher'
        : 'student';
    navigate(`/${base}/update-password`);
  };

  // Fetch unread homework count for students
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (role !== UserRole.STUDENT || !user_token) {
        setUnreadCount(0);
        return;
      }

      try {
        const { count, error } = await userOps.supabaseClient
          .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
          .select('*', { count: 'exact', head: true })
          .eq('student_token', user_token)
          .eq('completed', false);

        if (!error) {
          console.log('Unread homework count:', count);
          setUnreadCount(count || 0);
        } else {
          console.error('Error fetching unread homework count:', error);
        }
      } catch (err) {
        console.error('Error fetching unread homework count:', err);
      }
    };

    fetchUnreadCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [role, user_token, userOps.supabaseClient]);

  const bg = useColorModeValue('blue.50', 'blue.900');
  const color = useColorModeValue('blue.600', 'blue.300');

  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      right="0"
      zIndex="9999"
      bg={useColorModeValue('white', 'gray.900')}
      backdropFilter="blur(8px)"
      borderBottomWidth="1px"
      borderColor={useColorModeValue('gray.200', 'gray.700')}
      boxShadow={useColorModeValue(
        '0 1px 3px rgba(0, 0, 0, 0.1)',
        '0 1px 3px rgba(0, 0, 0, 0.3)',
      )}
      px={{ base: 4, md: 6 }}
      height="80px"
      {...rest}
    >
      <Flex justify="space-between" align="center" height="full">
        {/* Left Section - Logo and Brand */}
        <HStack spacing={{ base: 3, md: 4 }} align="center">
          <IconButton
            display={{ base: 'flex', md: 'none' }}
            onClick={onOpen}
            variant="ghost"
            size="md"
            aria-label="Open menu"
            icon={<FiMenu />}
            _hover={{
              bg: useColorModeValue('blue.50', 'blue.900'),
              color: useColorModeValue('blue.600', 'blue.300'),
            }}
          />

          <Box display="flex" alignItems="center" cursor="pointer">
            <Text
              fontSize={{ base: 'xl', md: '2xl' }}
              fontWeight="bold"
              fontFamily="heading"
              color={useColorModeValue('gray.800', 'white')}
            >
              泓河开卷&trade;
            </Text>
          </Box>
        </HStack>

        {/* Right Section - Actions and User Menu */}
        <HStack spacing={{ base: 2, md: 4 }} align="center">
          {/* Notifications */}
          <Box position="relative">
            <IconButton
              size="md"
              variant="ghost"
              aria-label="Notifications"
              icon={<FiBell />}
              _hover={{
                bg: useColorModeValue('gray.100', 'gray.700'),
                transform: 'translateY(-1px)',
              }}
              transition="all 0.2s"
              onClick={() => {
                if (role === UserRole.STUDENT) {
                  navigate('/student/pending');
                }
              }}
            />
            {/* Unread homework count badge */}
            {/* {unreadCount > 0 && (
              <Badge
                position="absolute"
                top="-1"
                right="-1"
                colorScheme="red"
                borderRadius="full"
                fontSize="xs"
                minW="20px"
                h="20px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontWeight="bold"
                boxShadow="0 2px 4px rgba(0,0,0,0.2)"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )} */}
          </Box>

          {/* User Menu */}
          <Menu>
            <MenuButton
              as={Box}
              py={2}
              px={3}
              rounded="xl"
              bg={useColorModeValue('gray.50', 'gray.800')}
              _hover={{
                bg: useColorModeValue('gray.100', 'gray.700'),
                transform: 'translateY(-1px)',
              }}
              _active={{
                bg: useColorModeValue('gray.200', 'gray.600'),
              }}
              transition="all 0.2s"
              cursor="pointer"
              border="1px solid"
              borderColor={useColorModeValue('gray.200', 'gray.600')}
            >
              <HStack spacing={3}>
                <Avatar
                  name={full_name}
                  size="sm"
                  bg={useColorModeValue('blue.500', 'blue.300')}
                  color="white"
                />
                <VStack
                  display={{ base: 'none', md: 'flex' }}
                  align="flex-start"
                  spacing="1px"
                >
                  <Text fontSize="sm" fontWeight="medium">
                    {first_name}
                  </Text>
                  <Text
                    fontSize="xs"
                    color={useColorModeValue('gray.500', 'gray.400')}
                  >
                    {role}
                  </Text>
                </VStack>
                <Box
                  display={{ base: 'none', md: 'flex' }}
                  color={useColorModeValue('gray.500', 'gray.400')}
                >
                  <FiChevronDown />
                </Box>
              </HStack>
            </MenuButton>

            <MenuList
              bg={useColorModeValue('white', 'gray.800')}
              borderColor={useColorModeValue('gray.200', 'gray.600')}
              borderRadius="xl"
              shadow="xl"
              py={3}
              px={2}
              minW="200px"
            >
              {/* Profile Section */}
              <Box px={3} py={2} mb={2}>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color={useColorModeValue('gray.900', 'white')}
                >
                  {full_name}
                </Text>
                <Text
                  fontSize="xs"
                  color={useColorModeValue('gray.500', 'gray.400')}
                >
                  {role}
                </Text>
              </Box>

              <MenuDivider />

              {role !== UserRole.STUDENT && (
                <>
                  <MenuItem
                    icon={<FiSettings />}
                    fontSize="sm"
                    borderRadius="lg"
                    mx={1}
                    _hover={{
                      bg: bg,
                      color: color,
                    }}
                    onClick={handleUpdatePassword}
                  >
                    Update Password
                  </MenuItem>

                  <MenuDivider />
                </>
              )}

              <MenuItem
                icon={<FiLogOut />}
                fontSize="sm"
                borderRadius="lg"
                mx={1}
                _hover={{
                  bg: useColorModeValue('red.50', 'red.900'),
                  color: useColorModeValue('red.600', 'red.300'),
                }}
                onClick={handleSignOut}
              >
                Sign Out
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>
    </Box>
  );
};
